import { useState, useEffect } from "react";
import { Minus, Plus, MessageCircle, Gift, Truck, Clock, ShieldCheck, CalendarCheck, ChevronDown } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal, Overline } from "./Reveal";
import { api, waLink } from "@/lib/api";
import { BookingDialog } from "./BookingDialog";
import { toast } from "sonner";

const FALLBACK_ANCHORS = [
  { persons: 10, price: 3200 }, { persons: 15, price: 3950 }, { persons: 20, price: 4650 },
  { persons: 25, price: 5400 }, { persons: 30, price: 6200 }, { persons: 40, price: 7800 },
  { persons: 50, price: 9300 }, { persons: 60, price: 10900 }, { persons: 70, price: 12900 },
  { persons: 100, price: 17400 },
];

const BULK_MENU = [
  { key: "paneer", label: "Paneer", pick: 1, options: ["Kadhai Paneer", "Paneer Butter Masala", "Matar Paneer", "Handi Paneer", "Paneer Lababdar"] },
  { key: "dryveg", label: "Dry Veg", pick: 1, options: ["Mix Veg", "Aloo Jeera", "Bhindi Masala", "Aloo Gobhi", "Khatta Meetha Kaddu"] },
  { key: "dal", label: "Dal", pick: 1, options: ["Dal Fry", "Dal Makhani", "Chola", "Kadhi", "Rajma"] },
  { key: "rice", label: "Rice", pick: 1, options: ["Jeera Rice", "Matar Pulao", "Veg Pulao", "Steam Rice"] },
  { key: "dessert", label: "Dessert", pick: 1, options: ["Gulab Jamun", "Dry Fruit Kheer", "Suji Halwa", "Fruit Custard"] },
  { key: "breads", label: "Breads", pick: 1, options: ["Pudi/Kachori", "Naan/Tandoori"] },
  { key: "raita", label: "Raita", pick: 1, options: ["Boondi Raita", "Mix Veg Raita"] },
  { key: "snacks", label: "Snacks", pick: 2, options: ["Noodles", "Manchurian", "Fried Rice", "Spring Roll", "Hara Bhara Kebab", "Dahi Kebab", "Veg Cutlet", "Honey Chilli Potato"] },
];

const inr = (n) => "₹" + n.toLocaleString("en-IN");

function computePrice(n, anchors) {
  const a = [...anchors].sort((x, y) => x.persons - y.persons);
  if (!a.length) return 0;
  const first = a[0], last = a[a.length - 1];
  if (n <= first.persons) return Math.round((first.price / first.persons) * n / 10) * 10;
  if (n >= last.persons) return Math.round((last.price / last.persons) * n / 10) * 10;
  for (let i = 0; i < a.length - 1; i++) {
    if (n >= a[i].persons && n <= a[i + 1].persons) {
      const t = (n - a[i].persons) / (a[i + 1].persons - a[i].persons);
      return Math.round((a[i].price + t * (a[i + 1].price - a[i].price)) / 10) * 10;
    }
  }
  return last.price;
}

const defaultSelections = (cats) => Object.fromEntries(cats.map((c) => [c.key, c.pick > 1 ? [] : ""]));

export function Calculator() {
  const [persons, setPersons] = useState(25);
  const [anchors, setAnchors] = useState(FALLBACK_ANCHORS);
  const [cats, setCats] = useState(BULK_MENU);
  const [sel, setSel] = useState(() => defaultSelections(BULK_MENU));
  const [bookingOpen, setBookingOpen] = useState(false);

  const chosen = (c) => {
    const v = sel[c.key];
    if (c.pick > 1) return Array.isArray(v) ? v : [];
    return v || "";
  };
  const catComplete = (c) => (c.pick > 1 ? chosen(c).length === c.pick : !!chosen(c));
  const bookingItems = cats.filter(catComplete).map((c) => ({
    category: c.label,
    dish: c.pick > 1 ? chosen(c).join(" + ") : chosen(c),
  }));

  useEffect(() => {
    api.get("/pricing").then((r) => {
      if (r.data?.length) setAnchors(r.data);
    }).catch(() => {});
    api.get("/bulk-categories").then((r) => {
      if (r.data?.length) {
        setCats(r.data);
        setSel((s) => ({ ...defaultSelections(r.data), ...Object.fromEntries(
          Object.entries(s).filter(([k]) => r.data.some((c) => c.key === k))
        ) }));
      }
    }).catch(() => {});
  }, []);

  const clamp = (v) => Math.max(10, Math.min(150, isNaN(v) ? 10 : v));
  const total = computePrice(persons, anchors);

  const message =
    `Hi The Quisine! I'd like a bulk food order.\n` +
    `• Persons: ${persons}\n` +
    cats.filter(catComplete).map((c) =>
      c.pick > 1 ? `• ${c.label} (any ${c.pick}): ${chosen(c).join(", ")}` : `• ${c.label}: ${chosen(c)}`
    ).join("\n") +
    `\n(Salad, Papad & Disposals complimentary)\n` +
    `• Estimated total: ${inr(total)}`;

  const openBooking = () => {
    const missing = cats.find((c) => !catComplete(c));
    if (missing) {
      toast.error(missing.pick > 1
        ? `Please pick any ${missing.pick} ${missing.label.toLowerCase()} first`
        : `Please select a ${missing.label.toLowerCase()} dish first`);
      return;
    }
    setBookingOpen(true);
  };

  const toggleChip = (c, opt) => {
    setSel((s) => {
      const cur = Array.isArray(s[c.key]) ? [...s[c.key]] : [];
      const i = cur.indexOf(opt);
      if (i >= 0) cur.splice(i, 1);
      else {
        if (cur.length >= c.pick) return s;
        cur.push(opt);
      }
      return { ...s, [c.key]: cur };
    });
  };

  return (
    <section id="calculator" className="px-5 py-16 bg-primary text-primary-foreground scroll-mt-16">
      <div className="mx-auto max-w-3xl">
        <Reveal>
          <Overline className="text-accent">Instant estimate</Overline>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight mt-3">Bulk Food Calculator</h2>
          <p className="mt-3 text-primary-foreground/70 text-sm">Pick your guests, choose one dish per category, and see the price instantly.</p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-8 rounded-3xl bg-background text-foreground p-6 sm:p-8">
            {/* Persons stepper */}
            <label className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">
              Number of persons
            </label>
            <div className="mt-3 flex items-center justify-between">
              <button
                data-testid="persons-minus"
                onClick={() => setPersons((p) => clamp(p - 5))}
                className="grid place-items-center w-14 h-14 rounded-full border border-border active:scale-95 transition-transform"
                aria-label="decrease"
              >
                <Minus className="w-6 h-6" />
              </button>
              <input
                data-testid="persons-value"
                type="number"
                value={persons}
                onChange={(e) => setPersons(clamp(parseInt(e.target.value || "0", 10)))}
                className="w-24 text-center font-serif text-5xl text-primary bg-transparent outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <button
                data-testid="persons-plus"
                onClick={() => setPersons((p) => clamp(p + 5))}
                className="grid place-items-center w-14 h-14 rounded-full bg-primary text-primary-foreground active:scale-95 transition-transform disabled:opacity-40"
                aria-label="increase"
                disabled={persons >= 150}
              >
                <Plus className="w-6 h-6" />
              </button>
            </div>
            <p data-testid="persons-cap-note" className="mt-2 text-center text-xs text-muted-foreground">Bulk orders for 10–150 persons. For larger events, contact us for custom catering.</p>

            {/* Menu selectors */}
            <label className="mt-8 block text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">
              Customize your package
            </label>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {cats.map((c) => (
                <div key={c.key} className={c.pick > 1 ? "sm:col-span-2" : ""}>
                  <span className="text-xs font-medium text-primary">
                    {c.label}{c.pick > 1 ? <span className="text-muted-foreground font-normal"> (pick any {c.pick})</span> : null}
                  </span>
                  {c.pick > 1 ? (
                    <div data-testid={`bulk-chips-${c.key}`} className="mt-1.5 flex flex-wrap gap-2">
                      {c.options.map((o) => {
                        const active = chosen(c).includes(o);
                        const maxed = !active && chosen(c).length >= c.pick;
                        return (
                          <button
                            key={o}
                            type="button"
                            data-testid={`bulk-chip-${c.key}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            onClick={() => toggleChip(c, o)}
                            disabled={maxed}
                            className={`h-11 px-4 rounded-full border text-base transition-colors disabled:opacity-40 ${
                              active
                                ? "bg-primary text-primary-foreground border-primary"
                                : "bg-secondary/40 border-border hover:border-primary/50"
                            }`}
                          >
                            {o}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="relative">
                      <select
                        data-testid={`bulk-select-${c.key}`}
                        value={chosen(c)}
                        onChange={(e) => setSel((s) => ({ ...s, [c.key]: e.target.value }))}
                        className={`mt-1 w-full h-12 rounded-xl border border-border bg-secondary/40 px-3 pr-9 text-base outline-none focus:border-primary appearance-none ${chosen(c) ? "" : "text-muted-foreground"}`}
                      >
                        <option value="" disabled>Select {c.label.toLowerCase()}…</option>
                        {c.options.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/4 w-4 h-4 text-primary/60" />
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-4 flex items-center gap-2 rounded-2xl bg-accent/15 text-foreground p-3">
              <Gift className="w-4 h-4 text-accent shrink-0" />
              <span className="text-xs">Salad, Papad & Disposals are <strong>complimentary</strong> with every order.</span>
            </div>

            {/* Result */}
            <div className="mt-6 rounded-2xl bg-primary text-primary-foreground p-6 flex items-center justify-between">
              <div>
                <div className="text-xs tracking-wide uppercase text-primary-foreground/70">Total price</div>
                <div className="text-xs text-primary-foreground/60 mt-0.5">for {persons} persons</div>
              </div>
              <motion.div key={total} initial={{ opacity: 0.4, y: 6 }} animate={{ opacity: 1, y: 0 }}
                data-testid="calc-total" className="font-serif text-5xl">{inr(total)}</motion.div>
            </div>

            {/* Trust strip */}
            <div data-testid="calc-trust-strip" className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 rounded-2xl bg-secondary/50 py-3 px-4">
              <span className="flex items-center gap-1.5 text-xs font-medium text-primary"><Truck className="w-4 h-4 text-accent" /> Free delivery</span>
              <span className="text-border">•</span>
              <span className="flex items-center gap-1.5 text-xs font-medium text-primary"><Clock className="w-4 h-4 text-accent" /> Same-day fresh</span>
              <span className="text-border">•</span>
              <span className="flex items-center gap-1.5 text-xs font-medium text-primary"><ShieldCheck className="w-4 h-4 text-accent" /> Hygienic kitchen</span>
            </div>

            <a
              data-testid="calc-whatsapp-btn"
              href={waLink(message)}
              target="_blank"
              rel="noreferrer"
              className="mt-4 h-14 w-full rounded-full bg-[#25D366] text-white font-medium grid place-items-center active:scale-[0.99] transition-transform"
            >
              <span className="flex items-center gap-2"><MessageCircle className="w-5 h-5" /> Get Quote on WhatsApp</span>
            </a>
            <button
              data-testid="calc-book-btn"
              onClick={openBooking}
              className="mt-3 h-14 w-full rounded-full bg-accent text-accent-foreground font-semibold grid place-items-center active:scale-[0.99] transition-transform"
            >
              <span className="flex items-center gap-2"><CalendarCheck className="w-5 h-5" /> Book Order · Pay ₹500 Advance</span>
            </button>
            <p className="mt-3 text-center text-xs text-muted-foreground">Bulk orders can be booked online. For catering, request a quote on WhatsApp.</p>
          </div>
        </Reveal>
      </div>
      <BookingDialog open={bookingOpen} onClose={() => setBookingOpen(false)} persons={persons} items={bookingItems} total={total} />
    </section>
  );
}
